/**
 * Landing top bar — sticky, clay-glass. Holds the wordmark, in-page anchors,
 * theme toggle, a "Sign in" route into the console chooser, and the primary
 * "Book a demo" CTA. Collapses to a sheet menu under md.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Moon, PawPrint, Sun, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { use_theme_store } from '@/lib/theme-store';
import { scrollToId } from './scroll-to';

const NAV_LINKS = [
  { label: 'Services', id: 'services' },
  { label: 'How it works', id: 'how-it-works' },
  { label: 'For vendors', id: 'audience' },
  { label: 'Loved by', id: 'social-proof' },
];

export function LandingNav() {
  const navigate = useNavigate();
  const dark = use_theme_store((s) => s.dark);
  const toggle_theme = use_theme_store((s) => s.toggle);
  const [open, set_open] = useState(false);

  function go(id: string) {
    set_open(false);
    scrollToId(id);
  }

  return (
    <header className="sticky top-0 z-40 border-b-2 border-brand-outline-variant/20 bg-background/80 backdrop-blur-md">
      <nav className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-5">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="flex items-center gap-2.5"
          aria-label="Pet Marketplace home"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm">
            <PawPrint className="h-5 w-5" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight">Pet Marketplace</span>
        </a>

        {/* Desktop links */}
        <div className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => go(l.id)}
              className="rounded-button px-3 py-2 text-sm font-semibold text-muted-foreground transition-colors duration-fast hover:bg-secondary hover:text-foreground"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle_theme}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="landing-theme-toggle"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="hidden sm:inline-flex"
            onClick={() => navigate('/choose')}
            data-testid="landing-sign-in"
          >
            Sign in
          </Button>
          <Button
            variant="gradient"
            size="sm"
            className="hidden sm:inline-flex"
            onClick={() => navigate('/choose')}
            data-testid="landing-nav-cta"
          >
            Get started
          </Button>
          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => set_open((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </nav>

      {/* Mobile sheet */}
      <div
        className={cn(
          'overflow-hidden border-t-2 border-brand-outline-variant/20 transition-all duration-normal ease-bounce md:hidden',
          open ? 'max-h-96' : 'max-h-0 border-t-0',
        )}
      >
        <div className="flex flex-col gap-1 px-5 py-4">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => go(l.id)}
              className="rounded-button px-3 py-3 text-left text-base font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              {l.label}
            </button>
          ))}
          <div className="mt-2 flex flex-col gap-2">
            <Button variant="outline" onClick={() => navigate('/choose')}>
              Sign in
            </Button>
            <Button variant="gradient" onClick={() => navigate('/choose')}>
              Get started
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
